import { ApexOptions } from 'apexcharts';
import React from 'react';
import ReactApexChart from 'react-apexcharts';
import { assign } from 'xstate';
import { cloneDeep } from 'lodash';
import { IntlShape } from 'react-intl';
import { Typography } from '@mui/material';

import { MachineContext } from '../../MachineContext';
import { BaseComponent } from '../Component';
import { translateWithFallback } from '../../util';
import { theme as evoachDefaultTheme } from '../../theme/theme';

export interface SeriesObject {
  name: string;
  data: number[];
  color: string;
}

export interface RadarchartDisplayProps {
  options?: ApexOptions;
  series: SeriesObject[];
  labels: string[];
  width?: number;
  title?: string;
}

export const RadarchartDisplay: React.FC<RadarchartDisplayProps> = ({
  options,
  series,
  labels,
  width,
  title,
}) => {
  return (
    <div>
      {title !== undefined && title.trim() !== '' && (
        <Typography
          variant="h5"
          style={{
            textAlign: 'center',
            fontWeight: 'bold',
            width: width ? width : '100%',
          }}
        >
          {title}
        </Typography>
      )}
      <ReactApexChart
        options={{ ...options, labels: labels }}
        series={series}
        type="radar"
        width={width ? width : '100%'}
        id="radarchart"
        key="radarchart"
      />
    </div>
  );
};

export interface RadarchartDisplayComponent extends BaseComponent {
  type: 'radarchart';
  props: RadarchartDisplayProps;
}

export const defaultRadarChartOptions: ApexOptions = {
  stroke: {
    width: 2,
  },
  chart: {
    background: '#f2f2f2',
    type: 'radar',
    dropShadow: {
      enabled: true,
      blur: 1,
      left: 1,
      top: 1,
    },
    toolbar: {
      show: false,
    },
  },
  colors: ['#EC504E', '#EBC856'],
  legend: {
    show: true,
    labels: {
      colors: ['#EC504E', '#EBC856'],
      useSeriesColors: true,
    },
    fontWeight: 400,
    fontSize: '14',
    itemMargin: {
      horizontal: 20,
    },
  },
  fill: {
    opacity: 0.1,
    colors: ['#EC504E', '#EBC856'],
  },
  plotOptions: {
    radar: {
      polygons: {
        strokeColors: '#FFFFFF',
      },
    },
  },
  yaxis: {
    show: false,
    max: 10,
    min: 0,
    tickAmount: 10,
  },
  grid: {
    position: 'front',
    yaxis: {
      lines: {
        show: false,
      },
    },
  },
  markers: {
    size: 0,
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200,
        },
        legend: {
          position: 'bottom',
        },
      },
    },
  ],
};

export const renderRadarchartDisplayAction = (intl: IntlShape) => {
  return assign((context: MachineContext, _event, actionMetadata) => {
    const newContext = cloneDeep(context);
    newContext.widgetData = newContext.widgetData.filter(
      (element) => !element.temporary
    );

    const payload = actionMetadata.action.payload;
    let radaroptions = payload.options;

    if (
      radaroptions === undefined ||
      radaroptions === '' ||
      Object.keys(radaroptions).length === 0
    ) {
      radaroptions = defaultRadarChartOptions;
    }

    // max number of segments (default is 10)
    if (payload.tickAmount) {
      radaroptions.yaxis.tickAmount = payload.tickAmount;
    }

    // if series payload is set, take that, if not, empty series
    let series: SeriesObject[] = payload.series ? payload.series : [];
    let labels: string[] = [];

    const getLabelsFrom = payload.getLabelsFrom;
    const radarLabels = payload.radarLabels;
    const getValuesFrom = payload.getValuesFrom;

    // in v2 of RadarChart State Entry in Builder, there might be two new variables available,
    if (
      radarLabels &&
      Array.isArray(payload.radarLabels) &&
      getValuesFrom &&
      Array.isArray(payload.getValuesFrom)
    ) {
      // auto-generation of labels + series
      series = [];
      const varname1 = payload.getValuesFrom[0];
      const varname2 = payload.getValuesFrom[1];

      if (
        context.userData[varname1] !== undefined &&
        context.userData[varname1] !== null
      ) {
        series.push({
          name: translateWithFallback(intl, radarLabels[0]),
          data: context.userData[varname1],
          color: evoachDefaultTheme.palette.primary.main,
        });
      }

      if (
        context.userData[varname2] !== undefined &&
        context.userData[varname2] !== null
      ) {
        series.push({
          name: translateWithFallback(intl, radarLabels[1]),
          data: context.userData[varname2],
          color: evoachDefaultTheme.palette.secondary.main,
        });
      }

      if (getLabelsFrom && getLabelsFrom.trim('') !== '') {
        labels = context.userData[getLabelsFrom].map((labelId: string) =>
          translateWithFallback(intl, labelId)
        );
      } else {
        labels = payload.labels.map((labelId: string) =>
          translateWithFallback(intl, labelId)
        );
      }
    } else {
      labels = payload.labels.map((labelId: string) =>
        translateWithFallback(intl, labelId)
      );
    }

    // push widget
    newContext.widgetData.push({
      type: 'radarchart',
      temporary: actionMetadata.action.temporary ?? false,
      props: {
        options: radaroptions as ApexOptions,
        series: series,
        labels: labels,
        width: payload.width,
        title: translateWithFallback(intl, payload.title),
      },
    });

    return newContext;
  });
};
